"use client";

import { LazyMotion, MotionConfig } from "framer-motion";
import type { ReactNode } from "react";

/**
 * Loads framer-motion's animation features once, for the whole tree.
 *
 * Importing `motion` pulls the entire library into the bundle — measured at
 * ~104KB gzip on the homepage, for nine call sites doing fades, scroll
 * transforms, a drawer and a modal. `LazyMotion` + the `m` component ship only
 * the parts actually used: `domAnimation` covers animation, variants, exit and
 * gestures, and leaves out layout animations and drag, which nothing here uses.
 *
 * `strict` on purpose: it makes `motion.div` throw at runtime, so the next
 * person to reach for it gets an error rather than silently pulling the full
 * library back in and undoing this.
 */
/**
 * Loaded as a dynamic import, not a static one. `features={domAnimation}`
 * would still bundle the feature set into the same chunk — LazyMotion only
 * splits it out when handed a loader.
 */
const loadFeatures = () => import("framer-motion").then((mod) => mod.domAnimation);

/**
 * `reducedMotion="user"` because framer-motion ignores the reduced-motion
 * setting by default, and the global CSS rule in `tools/sass/global/_motion.scss`
 * cannot reach animation started from JavaScript. With it, every `m.*` drops
 * its transform animations for a user who asked for less motion, and keeps
 * fades. A call site that checks `useReducedMotion` itself still can.
 */
const MotionProvider = ({ children }: { children: ReactNode }) => (
  <MotionConfig reducedMotion="user">
    <LazyMotion features={loadFeatures} strict>
      {children}
    </LazyMotion>
  </MotionConfig>
);

export default MotionProvider;
